import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BookOpen, ClipboardCheck, Settings, ArrowRight, Copy, Check, RefreshCw, Users, CreditCard, Share2 } from "lucide-react";

export function SchoolAdminDashboard({ school, pendingCount, copied, regenerating, onCopy, onRegenerate }) {
  return (
    <>
      {/* Admin Controls */}
      <div className="border border-border rounded-2xl p-5 mb-8">
        <p className="text-[0.75rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-4">
          Admin Controls
        </p>

        <div className="grid grid-cols-2 gap-3 max-[560px]:grid-cols-1">
          <Link to="/schools/classes" className="group flex items-center gap-4 px-5 py-[1.125rem] bg-card border border-border rounded-xl no-underline text-foreground transition-[border-color,box-shadow,transform] duration-150 hover:border-primary hover:shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:-translate-y-px">
            <div className="w-[42px] h-[42px] rounded-[10px] bg-accent flex items-center justify-center text-primary shrink-0"><BookOpen size={20} /></div>
            <div>
              <div className="text-[0.9375rem] font-semibold">All Classes</div>
              <div className="text-[0.8125rem] text-muted-foreground mt-[0.15rem]">View all classes in this school</div>
            </div>
            <ArrowRight size={15} className="ml-auto text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[3px] group-hover:text-primary" />
          </Link>

          <Link to="/schools/teacher-activity" className="group flex items-center gap-4 px-5 py-[1.125rem] bg-card border border-border rounded-xl no-underline text-foreground transition-[border-color,box-shadow,transform] duration-150 hover:border-primary hover:shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:-translate-y-px">
            <div className="w-[42px] h-[42px] rounded-[10px] bg-accent flex items-center justify-center text-primary shrink-0"><Users size={20} /></div>
            <div>
              <div className="text-[0.9375rem] font-semibold">Teacher Activity</div>
              <div className="text-[0.8125rem] text-muted-foreground mt-[0.15rem]">Usage stats and last active times</div>
            </div>
            <ArrowRight size={15} className="ml-auto text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[3px] group-hover:text-primary" />
          </Link>

          <Link to="/schools/settings" className="group flex items-center gap-4 px-5 py-[1.125rem] bg-card border border-border rounded-xl no-underline text-foreground transition-[border-color,box-shadow,transform] duration-150 hover:border-primary hover:shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:-translate-y-px">
            <div className="w-[42px] h-[42px] rounded-[10px] bg-accent flex items-center justify-center text-primary shrink-0"><Settings size={20} /></div>
            <div>
              <div className="text-[0.9375rem] font-semibold">Settings</div>
              <div className="text-[0.8125rem] text-muted-foreground mt-[0.15rem]">Manage school name and sharing policy</div>
            </div>
            <ArrowRight size={15} className="ml-auto text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[3px] group-hover:text-primary" />
          </Link>

          <Link to="/schools/seats" className="group flex items-center gap-4 px-5 py-[1.125rem] bg-card border border-border rounded-xl no-underline text-foreground transition-[border-color,box-shadow,transform] duration-150 hover:border-primary hover:shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:-translate-y-px">
            <div className="w-[42px] h-[42px] rounded-[10px] bg-accent flex items-center justify-center text-primary shrink-0"><CreditCard size={20} /></div>
            <div>
              <div className="text-[0.9375rem] font-semibold">Seat Management</div>
              <div className="text-[0.8125rem] text-muted-foreground mt-[0.15rem]">Assign seats and manage billing</div>
            </div>
            <ArrowRight size={15} className="ml-auto text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[3px] group-hover:text-primary" />
          </Link>

          <Link to="/schools/class-shares" className="group flex items-center gap-4 px-5 py-[1.125rem] bg-card border border-border rounded-xl no-underline text-foreground transition-[border-color,box-shadow,transform] duration-150 hover:border-primary hover:shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:-translate-y-px">
            <div className="w-[42px] h-[42px] rounded-[10px] bg-accent flex items-center justify-center text-primary shrink-0"><Share2 size={20} /></div>
            <div>
              <div className="text-[0.9375rem] font-semibold">Class Sharing</div>
              <div className="text-[0.8125rem] text-muted-foreground mt-[0.15rem]">See all active sharing in this school</div>
            </div>
            <ArrowRight size={15} className="ml-auto text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[3px] group-hover:text-primary" />
          </Link>

          {school?.sharingMode === "restricted" && (
            <Link to="/schools/share-requests" className="group flex items-center gap-4 px-5 py-[1.125rem] bg-card border border-border rounded-xl no-underline text-foreground transition-[border-color,box-shadow,transform] duration-150 hover:border-primary hover:shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:-translate-y-px">
              <div className="w-[42px] h-[42px] rounded-[10px] bg-accent flex items-center justify-center text-primary shrink-0" style={{ position: "relative" }}>
                <ClipboardCheck size={20} />
                {pendingCount > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -4,
                    width: 16, height: 16, borderRadius: "50%",
                    background: "hsl(var(--destructive))",
                    color: "hsl(var(--destructive-foreground))",
                    fontSize: 9, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {pendingCount > 9 ? "9+" : pendingCount}
                  </span>
                )}
              </div>
              <div>
                <div className="text-[0.9375rem] font-semibold">Share Requests</div>
                <div className="text-[0.8125rem] text-muted-foreground mt-[0.15rem]">
                  {pendingCount > 0
                    ? `${pendingCount} pending approval${pendingCount !== 1 ? "s" : ""}`
                    : "No pending requests"}
                </div>
              </div>
              <ArrowRight size={15} className="ml-auto text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[3px] group-hover:text-primary" />
            </Link>
          )}
        </div>
      </div>

      {/* Invite code */}
      {school?.inviteCode && (
        <div className="bg-card border border-border rounded-xl px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">Invite code</span>
            <code className="text-sm font-mono tracking-widest bg-muted px-2.5 py-1 rounded-lg select-all">
              {school.inviteCode}
            </code>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={onCopy}>
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button
              variant="ghost" size="sm"
              className="h-8 gap-1.5 text-muted-foreground hover:text-foreground text-xs"
              onClick={onRegenerate} disabled={regenerating}
            >
              <RefreshCw size={12} className={regenerating ? "animate-spin" : ""} />
              {regenerating ? "..." : "Regenerate"}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
